/**
 * Master Data Manager — HR/Admin curate the campus reference lists per institute.
 * Create / rename / activate / delete values for Department, Designation, Category,
 * Appointment Type, Pay Scale, Bank, Qualification and Blood Group. Lists marked
 * "required" block Add/Upload of staff whose value isn't on the list.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  ArrowLeft, Plus, Trash2, Pencil, Check, X, Loader2, DownloadCloud, ShieldCheck,
  ListChecks, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../../lib/apiBase';
import { safeJson } from '../../lib/safeJson';

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

export function MasterDataManager({ entityId, onBack }) {
  const [meta, setMeta] = useState([]);
  const [entries, setEntries] = useState({});
  const [active, setActive] = useState('department');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [editing, setEditing] = useState(null);     // entry_id
  const [editValue, setEditValue] = useState('');

  const load = useCallback(async () => {
    if (!entityId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/master-data?entity_id=${entityId}`,
        { headers: headers(), credentials: 'include' });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Could not load master data');
      setMeta(d.meta || []);
      setEntries(d.entries || {});
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  const activeMeta = useMemo(() => meta.find((m) => m.key === active) || {}, [meta, active]);
  const list = entries[active] || [];

  const addValue = async () => {
    const v = newValue.trim();
    if (!v) { toast.error('Type a value first'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/master-data`, {
        method: 'POST', headers: headers(), credentials: 'include',
        body: JSON.stringify({ entity_id: entityId, type: active, value: v }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Could not add');
      toast.success(d.message);
      setNewValue('');
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const saveEdit = async (entry) => {
    const v = editValue.trim();
    if (!v) { toast.error('Value cannot be empty'); return; }
    if (v === entry.value) { setEditing(null); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/master-data/${entry.entry_id}`, {
        method: 'PUT', headers: headers(), credentials: 'include',
        body: JSON.stringify({ value: v }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Rename failed');
      toast.success(d.message);
      setEditing(null);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const toggleActive = async (entry) => {
    try {
      const res = await fetch(`${API_URL}/api/master-data/${entry.entry_id}`, {
        method: 'PUT', headers: headers(), credentials: 'include',
        body: JSON.stringify({ active: !(entry.active !== false) }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (entry) => {
    const force = entry.in_use > 0;
    const msg = force
      ? `${entry.in_use} staff still use "${entry.value}". Force delete anyway? (their records keep the value until reassigned)`
      : `Delete "${entry.value}"?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await fetch(`${API_URL}/api/master-data/${entry.entry_id}?force=${force}`,
        { method: 'DELETE', headers: headers(), credentials: 'include' });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Delete failed');
      toast.success(d.message);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const importExisting = async () => {
    if (!window.confirm('Scan current staff and add their existing Department / Designation / etc. to these lists? This will not change any staff record.')) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/master-data/import-existing?entity_id=${entityId}`,
        { method: 'POST', headers: headers(), credentials: 'include' });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Import failed');
      toast.success(d.message);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="master-data-manager">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <ListChecks className="w-6 h-6 text-[#DC2626]" />
            Master Data
          </h1>
          <p className="text-sm text-slate-500">
            One clean set of values per institute. Required lists control what new & uploaded staff can use.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="masterdata-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Staff Directory
          </Button>
          <Button variant="outline" size="sm" onClick={importExisting} disabled={busy || !entityId}
            className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
            data-testid="masterdata-import-btn">
            <DownloadCloud className="w-4 h-4 mr-1" /> Import from current staff
          </Button>
        </div>
      </div>

      {!entityId && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="masterdata-no-entity">
          Pick an Institute / Branch in the top selector to manage its master data.
        </div>
      )}

      {entityId && (
        <Card>
          <CardContent className="p-3 space-y-3">
            {/* type pills */}
            <div className="flex flex-wrap items-center gap-1.5">
              {meta.map((m) => (
                <button key={m.key} type="button" onClick={() => { setActive(m.key); setEditing(null); setNewValue(''); }}
                  data-testid={`masterdata-type-${m.key}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                    active === m.key
                      ? 'bg-[#DC2626] border-[#DC2626] text-white'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-[#DC2626]'
                  }`}>
                  {m.enforced && <Lock className="w-3 h-3" />}
                  {m.label}
                  <span className={`ml-0.5 rounded-full px-1.5 text-[10px] ${active === m.key ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>
                    {m.count}
                  </span>
                </button>
              ))}
            </div>

            {/* enforcement banner */}
            <div className={`rounded-md border p-2 text-xs flex items-center gap-2 ${
              activeMeta.enforced ? 'border-[#DC2626]/30 bg-red-50 text-red-800' : 'border-slate-200 bg-slate-50 text-slate-600'}`}
              data-testid="masterdata-enforce-banner">
              <ShieldCheck className="w-4 h-4 shrink-0" />
              {activeMeta.enforced ? (
                <span><strong>Required list.</strong> When this list has values, Add Staff & Bulk Upload will
                  reject any {activeMeta.label?.toLowerCase()} that isn't on it — keeping your data consistent everywhere.</span>
              ) : (
                <span>Reference list for dropdowns & filters. Not enforced on staff upload.</span>
              )}
            </div>

            {/* add new */}
            <div className="flex items-center gap-2">
              <Input value={newValue} onChange={(e) => setNewValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addValue(); }}
                placeholder={`Add a new ${activeMeta.label || 'value'}…`}
                className="h-9 max-w-sm text-sm" data-testid="masterdata-new-input" />
              <Button size="sm" onClick={addValue} disabled={busy || !newValue.trim()}
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="masterdata-add-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>

            {/* entries */}
            {loading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => <div key={i} className="h-9 bg-slate-100 rounded animate-pulse" />)}
              </div>
            ) : list.length === 0 ? (
              <div className="text-center py-10 text-slate-500 text-sm" data-testid="masterdata-empty">
                No {activeMeta.label?.toLowerCase()} values yet. Add one above, or import from current staff.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-md"
                data-testid={`masterdata-list-${active}`}>
                {list.map((entry) => (
                  <div key={entry.entry_id}
                    className={`flex items-center gap-2 px-3 py-2 ${entry.active === false ? 'opacity-50' : ''}`}
                    data-testid={`masterdata-row-${entry.entry_id}`}>
                    {editing === entry.entry_id ? (
                      <>
                        <Input value={editValue} onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(entry); if (e.key === 'Escape') setEditing(null); }}
                          className="h-8 max-w-xs text-sm" autoFocus
                          data-testid={`masterdata-edit-input-${entry.entry_id}`} />
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600"
                          onClick={() => saveEdit(entry)} data-testid={`masterdata-edit-save-${entry.entry_id}`}>
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500"
                          onClick={() => setEditing(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm font-medium text-slate-800">{entry.value}</span>
                        {entry.in_use > 0 && (
                          <Badge variant="outline" className="text-[10px] text-slate-500">
                            {entry.in_use} in use
                          </Badge>
                        )}
                        {entry.active === false && (
                          <Badge className="bg-slate-200 text-slate-600 text-[10px]">inactive</Badge>
                        )}
                        <button type="button" onClick={() => toggleActive(entry)}
                          title={entry.active === false ? 'Activate' : 'Deactivate'}
                          className="text-[10px] text-slate-400 hover:text-slate-700 underline"
                          data-testid={`masterdata-toggle-${entry.entry_id}`}>
                          {entry.active === false ? 'activate' : 'disable'}
                        </button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500"
                          onClick={() => { setEditing(entry.entry_id); setEditValue(entry.value); }}
                          data-testid={`masterdata-edit-${entry.entry_id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#DC2626]"
                          onClick={() => remove(entry)}
                          data-testid={`masterdata-delete-${entry.entry_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-slate-500">
              Renaming a value updates every staff record of this institute that uses it, so filters and
              reports stay consistent. Values in use can't be deleted until staff are reassigned.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default MasterDataManager;
